import React, { useEffect, useState } from "react";
import { userService } from "../../services/UserService";
import { useParams } from "react-router";

import { Button, CircularProgress, Grid } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import * as echarts from "echarts";

const classes = {
    container: {
        paddingTop: "1rem",
    },
    dateSelectContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "whitesmoke",
        borderRadius: "4px",
        border: "1px solid #e0e0e0",
    },
    dataRequestButton: {
        "&:hover": {
            cursor: "pointer",
        },
    },
    chartContainer: {
        height: "600px",
        width: "900px",
        marginTop: "2rem",
    },
    spinnerContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        left: "50%",
        marginLeft: "-50px",
        top: "50%",
        marginTop: "-50px",
    },
};

const eventTypes = [
    "statisticsTab",
    "alertTab",
    "chartTab",
    "dashboard",
    "exportCSV",
    "line",
    "map",
    "path",
    "profile",
    "site",
    "siteInfoTab",
    "summaryTab",
    "visualizationTab",
];
const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const colors = [
    "#00A5E3",
    "#FC6238",
    "#C05780",
    "#FFD872",
    "#4DD091",
    "#FF96C5",
    "#F2D4CC",
    "#FF5768",
    "#00B0BA",
    "#FFA23A",
    "#6C88C4",
    "#CFF800",
    "#E77577",
];

export default function UserAnalyticsShow() {
    const { id } = useParams();

    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState(null);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [openStart, setOpenStart] = useState(false);
    const [openEnd, setOpenEnd] = useState(false);

    useEffect(() => {
        let chart;

        if (userData?.length) {
            if (!chart) chart = echarts.init(document.getElementById("main"));

            let seriesData = userData.map((data, idx) => {
                let eventName = eventTypes[idx];
                let color = colors[idx];

                return {
                    name: eventName,
                    type: "bar",
                    stack: "stack",
                    label: {
                        show: true,
                        position: "insideRight",
                    },
                    itemStyle: {
                        color: color,
                    },
                    data: data,
                };
            });

            // specify chart configuration item and data
            let option = {
                title: {
                    text: "",
                },
                tooltip: {
                    trigger: "axis",
                    axisPointer: {
                        type: "shadow",
                    },
                },
                grid: {
                    left: "3%",
                    right: "4%",
                    bottom: "3%",
                    containLabel: true,
                },
                legend: {
                    data: eventTypes,
                },
                xAxis: {
                    type: "category",
                    data: months,
                },
                yAxis: {
                    type: "value",
                },
                series: seriesData,
            };

            // use configuration item and data specified to show chart
            chart.setOption(option);

            setLoading(false);
        }
    }, [userData]);

    const handleOpenStart = (bool) => {
        setOpenStart(bool);
    };

    const handleOpenEnd = (bool) => {
        setOpenEnd(bool);
    };

    const handleStartDateChange = (date) => {
        setOpenStart(false);
        setOpenEnd(true);
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setOpenEnd(false);
        setEndDate(date);
    };

    const handleDataFetch = () => {
        setLoading(true);

        userService
            .getUserAnalytics(id, startDate, endDate)
            .then((data) => {
                let result = formatData(data);

                setUserData(result);
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const formatData = (data) => {
        let monthlyCount = {};

        eventTypes.forEach((event) => {
            monthlyCount[event] = new Array(12).fill(0);
        });

        data.forEach((d) => {
            let { dttm, event_name } = d;

            if (eventTypes.includes(event_name)) {
                let month = moment(dttm).month();

                monthlyCount[event_name][month] += 1;
            }
        });

        return Object.values(monthlyCount);
    };

    return (
        <div>
            <div sx={classes.container}>
                <Grid item xs={12} md={12} lg={6} sx={classes.dateSelectContainer}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid item xs={5} md={5} lg={5}>
                            <DatePicker
                                open={openStart}
                                onOpen={() => handleOpenStart(true)}
                                onClose={() => handleOpenStart(false)}
                                variant="inline"
                                format="MMM dd, yyyy"
                                margin="normal"
                                id="date-picker-inline-start"
                                label="Start Date"
                                value={startDate}
                                maxDate={new Date()}
                                onChange={handleStartDateChange}
                                KeyboardButtonProps={{
                                    "aria-label": "change date",
                                }}
                                PopoverProps={{
                                    anchorOrigin: { horizontal: "right", vertical: "bottom" },
                                    transformOrigin: { horizontal: "left", vertical: "top" },
                                }}
                                style={{ width: "70%" }}
                            />
                        </Grid>

                        <Grid item xs={5} md={5} lg={5}>
                            <DatePicker
                                autoOk={false}
                                open={openEnd}
                                onOpen={() => handleOpenEnd(true)}
                                onClose={() => handleOpenEnd(false)}
                                variant="inline"
                                format="MMM dd, yyyy"
                                margin="normal"
                                id="date-picker-inline-end"
                                label="End Date"
                                value={endDate}
                                maxDate={new Date()}
                                onChange={handleEndDateChange}
                                PopoverProps={{
                                    anchorOrigin: { horizontal: "right", vertical: "bottom" },
                                    transformOrigin: { horizontal: "left", vertical: "top" },
                                }}
                                style={{ width: "70%" }}
                            />
                        </Grid>
                    </LocalizationProvider>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleDataFetch}
                        sx={classes.dataRequestButton}
                    >
                        Go
                    </Button>
                </Grid>
            </div>

            <div id="main" sx={classes.chartContainer}></div>

            {loading && (
                <div sx={classes.spinnerContainer}>
                    <CircularProgress size={"4rem"} />
                </div>
            )}
        </div>
    );
}
